import {isEmpty, isEqual, get} from 'lodash';
import {EventBus} from "../utils/event-bus";
import StripeUtils from "../utils/stripe";
import Wallkit from '../plugins/wallkit.esm.min.js';
import store from "../store";
import {initFacebookSDK, initGoogleSDK} from '../utils/socials';
import StripeManager from "../managers/StripeManager";
import debug from "../utils/debug";

let event_tmp = {};

export default {
  data: function () {
    return {
      stripe: null,
      square: null,
      isCompReady: false,
      stripeElements: null,
    }
  },
  created() {
    /**
     * Event subscribe on component load state
     */
    EventBus.$on('wk-lazy-comp-ready', (value) => {
      this.isCompReady = value;
    });
  },
  mixins: [StripeManager],
  methods: {

    /**
     *
     */
    initStripeToken() {
      this.stripe = Stripe(Wallkit.resource.stripe_public_key);
    },

    /**
     * SEND PUBLIC EVENT
     * @param name
     * @param value
     * @param allow_duplication
     */
    event(name, value, allow_duplication) {
      let data = {
        name: name,
        value: value
      };

      if (isEqual(data, event_tmp) && !allow_duplication) {
        return;
      }
      event_tmp = data;
      top.postMessage(data, '*');
      debug.log("EVENT", data);
    },

    /**
     * Send resize window for outer change iframe
     */
    async resize(allow_duplication) {
      await this.$nextTick();

      const wallkitContent = document.getElementById("wallkit-content");
      if (!wallkitContent) {
        return;
      }

      const height = wallkitContent.scrollHeight;
      const width = wallkitContent.scrollWidth;

      this.event("wk-event-resize", {height, width}, allow_duplication);
    },

    resizeContent(element) {
      const {scrollHeight: height, scrollWidth: width} = element;
      debug.log('Content Resize - height:', height, ' width:', width);
      this.event("wk-event-resize", {height, width}, true);
    },

    /**
     * Send event for close outer modal
     */
    closeModal() {
      this.event("wk-event-close-modal", null);
    },

    /**
     * @private
     * Search element in template
     */
    $_get_element_in_template(element) {
      let el = null;
      if (document.getElementById(element.replace(/#/g, ''))) {
        el = document.getElementById(element.replace(/#/g, ''))
      } else if (document.getElementsByClassName(element)) {
        el = document.getElementById(element)
      }
      return el;
    },

    /**
     * @private
     * Stripe Card Form default styles getter
     */
    $_get_default_card_form_styles() {
      return {
        base: {
          color: '#684287',
          lineHeight: '18px',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      };
    },

    /**
     * @public
     * Toggle loader
     */
    toggleLoader(state) {
      EventBus.$emit('wk-loader', state);
    },

    /**
     *  Wait for template object get ready
     *
     * @returns {Promise<unknown>}
     */
    onTemplateReady() {
      return new Promise((resolve, reject) => {
        let intervalId = setInterval(() => {
          if (this.template !== null && typeof this.template !== 'undefined') {
            clearInterval(intervalId);
            resolve();
          }
        }, 10);
      });
    },

    onModalReady(callback) {
      if (this.isCompReady === true) {
        callback();
      } else {
        const onReady = () => {
          callback();
          EventBus.$off('wk-lazy-comp-ready', onReady);
        };
        EventBus.$on('wk-lazy-comp-ready', onReady);
      }
    },

    onFirebaseInit() {
      return new Promise((resolve) => {
        if (this.$firebase) {
          resolve();
        } else {
          const onReady = () => {
            resolve();
            EventBus.$off('firebase-init', onReady);
          };
          EventBus.$on('firebase-init', onReady);
        }
      });
    },

    socialsAuth(elementId = "#socials-buttons", callback, options = {}) {
      const renderElements = () => {
        if (!elementId) throw 'Element Id is not provided as argument';

        let elementsWrapper = this.$_get_element_in_template(elementId);

        if (elementsWrapper) {
          const googleElement = '<div id="google-sign-in"></div>';
          const facebookElement = '<div id="facebook-sign-in"></div>';

          elementsWrapper.innerHTML = googleElement + facebookElement;

          const facebookOptions = options.facebook || {};
          const googleOptions = options.google || {};

          this.facebookAuth('facebook-sign-in', facebookOptions, (response) => callback({
            method: 'facebook',
            data: response
          }));
          this.googleAuth('google-sign-in', googleOptions, (response) => callback({method: 'google', data: response}));
        }
      };

      this.onModalReady(renderElements);
    },

    facebookAuth(elementId = 'facebook-sign-in', attributes, callback) {
      const renderFacebookButton = () => {
        const elementWrapper = this.$_get_element_in_template(elementId);
        if (elementId && !elementWrapper) {
          return;
        }

        const button = document.createElement('fb:login-button');

        const buttonAttributes = {
          'button-type': 'login_with',
          'size': 'large',
          'width': '240',
          'scope': 'public_profile,email,user_hometown,user_location',
          'autologoutlink': false,
          'class': 'fb-login-button my-2',
          ...attributes
        };

        for (let key in buttonAttributes) {
          button.setAttribute(key, buttonAttributes[key]);
        }

        elementWrapper.parentNode.replaceChild(button, elementWrapper);

        FB.XFBML.parse();

      };

      initFacebookSDK(() => {
        FB.Event.unsubscribe('auth.authResponseChange', window.onFacebookLogin);

        window.onFacebookLogin = ({authResponse, status}) => {
          if (status === "connected") {
            debug.log('FACEBOOK AUTH RESPONSE:', authResponse);

            const access_token = authResponse ? authResponse.accessToken : null;

            FB.api('me?fields=first_name,last_name,email,hometown{location{city,state,country,country_code}},location{location{city,state,country,country_code}}', (response) => {
              if (response.error) {
                callback({status: "failed", error: response.error});
                return;
              }

              let user_location = {};

              if (response.location || response.hometown) {
                user_location = response.location ? response.location.location : response.hometown.location;
              }

              callback({access_token, ...response, user_location, status: 'success'})
            });
          } else {
            callback({status: "failed", error: 'User closed the modal or something went wrong.'});
          }
        };

        FB.Event.subscribe('auth.authResponseChange', window.onFacebookLogin);

        this.onModalReady(renderFacebookButton);
      });
    },

    async initFirebaseAuth(config, callback) {
      await this.onFirebaseInit();

      let params = {
        elementId: 'firebaseui-auth-container',
        providers: ['google', 'email']
      };

      if (config) {
        params = {
          ...params,
          ...config
        }
      }

      const allowedProviders = {
        'google': this.$firebase.auth.GoogleAuthProvider.PROVIDER_ID,
        'email': this.$firebase.auth.EmailAuthProvider.PROVIDER_ID,
        'facebook': this.$firebase.auth.FacebookAuthProvider.PROVIDER_ID,
        'microsoft': 'microsoft.com',
      };

      let selectedProviders = [];
      if (Array.isArray(params.providers)) {
        selectedProviders = params.providers.map((item) => {
          if (allowedProviders[item]) {
            return allowedProviders[item];
          }
        })
          .filter((item) => !!item);
      }

      const handleSuccessAuthCallback = (user) => {
        if (callback) {
          user.getIdToken().then((token) => {
            callback({status: 'success', data: {user, token}});
            // this.event('wk-firebase-token', token);
          });
        }
      };

      const callbacks = {
        signInSuccessWithAuthResult: function (authStatus) {
          if (authStatus) {
            handleSuccessAuthCallback(authStatus.user);
          }
        },
        signInFailure: function (error) {
          callback({status: 'failure', data: {error}});
        }
      };

      const firebaseUIConfig = {
        signInOptions: selectedProviders,
        signInFlow: 'popup',
        credentialHelper: 'none',
        callbacks
      };

      const init = () => {

        // Check if user already logged in.
        const unsubscribeAuthState = this.$firebase.auth().onAuthStateChanged(function (user) {
          if (user) {
            handleSuccessAuthCallback(user);
          }

          unsubscribeAuthState();
        });

        if (!window.firebaseUIInstance) {
          window.firebaseUIInstance = new window.firebaseui.auth.AuthUI(this.$firebase.auth());
        }

        window.firebaseUIInstance.start(`#${params.elementId}`, firebaseUIConfig);

      };

      this.onModalReady(init);
    },

    googleAuth(elementId = 'google-sign-in', attributes, callback) {
      const onSuccess = (response) => {
        debug.log('GOOGLE AUTH RESPONSE:', response);

        const authResponse = response.getAuthResponse();

        const access_token = authResponse ? authResponse.access_token : null;

        if (access_token) {
          const profile = response.getBasicProfile();

          const user = {
            id: profile.getId(),
            email: profile.getEmail(),
            first_name: profile.getGivenName(),
            last_name: profile.getFamilyName()
          };

          callback({access_token, ...user, status: 'success'});
        }
      };

      const onFailure = (response) => {
        debug.log('FAILED AUTH RESPONSE:', response);

        callback({...response, status: 'failed'});
      };

      const renderGoogleButton = () => {
        if (elementId && !this.$_get_element_in_template(elementId)) {
          return;
        }

        gapi.signin2.render(elementId, {
          'scope': 'profile email openid',
          'longtitle': true,
          'theme': 'light',
          'width': '240',
          'class': 'my-1',
          'onsuccess': onSuccess,
          'onfailure': onFailure,
          ...attributes
        });
      };

      initGoogleSDK(() => this.onModalReady(renderGoogleButton));
    },

    /**
     * @private
     * Search element in template
     */
    stripeInit(element_name) {

      if (!this.stripe) {
        this.initStripeToken();
      }

      if (typeof element_name === "undefined") {
        throw new Error("Undefined element in stripeInit(undefined)");
      }

      let getElement = (element) => this.$_get_element_in_template(element);

      let createErrorElement = (targetElement) => {
        let el = getElement(targetElement);

        if (typeof el !== "undefined" && el !== null) {
          let textElement = document.createElement("h5");

          textElement.setAttribute('id', "stripe-error");
          textElement.style.color = "red";
          textElement.style.fontSize = "13px";
          textElement.style.marginTop = "10px";

          el.appendChild(textElement);

          return textElement;
        }

      };

      let showError = (error) => {
        let el = getElement("#stripe-error");
        if (typeof el !== "undefined" && el !== null) {
          el.innerText = error;
        }
        this.toggleLoader(false);
      };

      let clearError = () => {
        showError('');
      };

      let handleCardSetup = (response, customer) => {

        StripeUtils.handle3dSecureModal()
          .then(() => {
            this.toggleLoader(false);
          })
          .catch(() => debug.log('closed'));

        return this.stripe.handleCardSetup(
          response.client_secret,
          card
        ).then((response) => {
          if (response.error) {
            this.removeStripeCustomer(customer.id);
            showError(response.error.message);
          } else {
            return handlePaymentResponse(response, customer);
          }
        });
      };

      let handlePaymentResponse = async (response, customer) => {
        if (!customer) throw new Error('Customer Not Found');

        if (response.error) {

          showError(response.error.message)

        } else if (response.stripe_intent && response.stripe_intent.status === "requires_payment_method") {

          const {stripe_intent} = response;

          if (!stripe_intent) throw new Error('No Stripe Intent Provided');

          handleCardSetup(stripe_intent, customer);

        } else {

          const {setupIntent: intent} = response;

          if (!intent) throw new Error('No Setup Intent Provided');

          let confirmResponse = await this.confirmStripePaymentMethod({intent, customer});

          if (confirmResponse) {
            // event sent to reload component
            EventBus.$emit('wk-lazy-comp-reload', true);
          }

          this.toggleLoader(false);
        }
      };

      if (!this.stripe || this.stripe === null || typeof this.stripe === "undefined") {
        showError('Stripe is not configured!');
        return;
      }

      const style = this.$_get_default_card_form_styles();

      const elements = this.stripe.elements();
      const card = elements.create('card', {hidePostalCode: true, style: style});

      card.on('ready', () => createErrorElement(element_name));

      let mountCard = (element_name) => {

        debug.log("CARD MOUNT ON ", element_name);

        if (getElement(element_name) !== null) {
          card.mount(element_name);

          card.addEventListener('change', async (event) => {
            clearError();

            debug.log("CARD VALID:", event.complete);

            if (event.complete) {

              this.toggleLoader(true);

              try {

                const data = await Wallkit.cardAttemptAttach();

                if (!data.success) {
                  throw new Error('Locked for 2 min, limit for attempts');
                }

                let {token, error} = await this.stripe.createToken(card);

                if (error) {
                  throw new Error(error.message);
                }

                let customer = await this.addStripeToken(token.id);

                let setupIntentResponse = await this.setupStripeIntent(customer);

                await handlePaymentResponse(setupIntentResponse, customer);

              } catch (error) {
                debug.log('Stripe on card input error:', error);

                if (error.error && error.error_description) {
                  showError(error.error_description);
                } else {
                  showError(error);
                }
              }
            }

          });
        }
      };

      this.onModalReady(() => mountCard(element_name));
    },

    isAvailableApplePay() {
      return store.getters['options/available_applepay'];
    },

    openApplePayPage(purchases) {

      return new Promise((resolve, reject) => {

        purchases = JSON.stringify(purchases);
        let applyPayWindow = window.open("https://wallkit.net/checkout?purchases=" + purchases + "&SPK=" + Wallkit.resource.stripe_public_key,
          "_blank",
          "width=375,height=667,menubar=no");

        window.addEventListener("message", (event) => {
          if (event.origin === "https://wallkit.net" && event.data.name === "applePay") {
            if (typeof event.data !== "undefined" && typeof event.data.token !== "undefined") {
              Wallkit.createStripeByToken(event.data.token)
                .then(data => {
                  resolve(data);
                })
                .catch(e => {
                  reject(e);
                })
            } else {
              throw new Error("Create stripe token is failed.");
            }
          } else {
            throw new Error("Incorrect request origin.");
          }
        })

      });


    },


    stripeInitPromise(element_name, callback, style) {

      let callbacks = [];
      const ___this = this;

      let __methods = {
        on: function (event, callback) {
          callbacks.push({"event": event, "callback": callback})
        }
      };

      if (typeof callback === "function") {
        __methods.on("valid_card", callback);
      } else if (typeof callback === "object" && typeof callback.event !== "undefined") {
        callbacks.push(callback)
      }

      return new Promise(function (resolve, reject) {

        /**
         * dispatch Event
         * @param event
         * @param params
         */
        let dispatchEvent = function (event, params) {
          for (var k in callbacks) {
            if (
              typeof callbacks[k] === "object" &&
              typeof callbacks[k].event !== "undefined" &&
              callbacks[k].event === event &&
              typeof callbacks[k].callback !== "undefined" &&
              typeof callbacks[k].callback === "function"
            ) {
              callbacks[k].callback(params);
            }
          }
        };

        if (typeof Wallkit.resource !== "undefined" && !isEmpty(Wallkit.resource.stripe_public_key)) {
          ___this.stripe = Stripe(Wallkit.resource.stripe_public_key);
        } else {
          let error = new Error("Wallkit stripe is not configured.");
          error.error_description = "Wallkit stripe is not configured.";
          dispatchEvent("error", error);
          reject(error);
        }

        if (typeof element_name === "undefined" || !element_name) {
          let error = new Error("Undefined element in stripeInitPromise(undefined)");
          error.error_description = "Undefined element in stripeInitPromise(undefined)";
          dispatchEvent("error", error);
          reject(error)
        }

        if (!___this.stripe || typeof ___this.stripe === "undefined") {
          let error = new Error("Stripe is not configured.");
          error.error_description = "Stripe is not configured.";
          dispatchEvent("error", error);
          reject(error)
        }

        let renderCardElement = (element_name) => {
          /**
           * get element
           */
          let el = ___this.$_get_element_in_template(element_name);

          if (el == null || typeof el === "undefined") {
            let error = new Error("Could not find specified element in template.");
            error.error_description = "Could not find specified element in template.";
            dispatchEvent("error", error);
            reject(error)
          }

          /**
           * Card form style default
           */
          if (!style || typeof style !== "object") {
            style = ___this.$_get_default_card_form_styles();
          }

          const elements = ___this.stripe.elements();
          const card = elements.create('card', {hidePostalCode: true, style: style});
          card.mount(element_name);

          dispatchEvent("mounted", card);

          card.addEventListener('change', async (event) => {

            if (event.complete) {
              try {
                const data = await Wallkit.cardAttemptAttach();

                if (!data.success) {
                  let error = new Error("Locked for 2 min, limit for attempts.");
                  error.error_description = "Locked for 2 min, limit for attempts.";
                  dispatchEvent("error", error);

                  reject(error);
                }

                dispatchEvent("valid_card", card);
              } catch (error) {
                reject(error);
              }
            } else {
              dispatchEvent("invalid_card", card);
            }

          });

          resolve(card);
        };

        /**
         * Render card element or subscribe to event for render it after template loaded
         */
        ___this.onModalReady(() => renderCardElement(element_name));

        setTimeout(() => {
          let error = new Error("Waiting for template download is timeout");
          error.error_description = "Waiting for template download is timeout";
          dispatchEvent("error", error);
          reject(error)
        }, 10000)

      });


    },


    /**
     * Create stripe customer by stripe token card
     * @param token
     */
    async addStripeToken(token) {
      return Wallkit.createStripeByToken(token);
    },

    /**
     * Confirm stripe payment method by stripe Payment Method Id
     * @param payment_method_id
     */
    async confirmStripePaymentMethod(payment_method_id) {
      return Wallkit.confirmStripePaymentMethod(payment_method_id);
    },

    /**
     * Confirm card action
     * @param payment_intent_id
     */
    async confirmCardAction(payment_intent_id) {
      return Wallkit.confirmStripePaymentIntent(payment_intent_id);
    },

    /**
     * Get Setup Intent
     * @param customer
     */
    async setupStripeIntent(customer) {
      return await Wallkit.setupStripeIntent(customer);
    },

    /**
     * Remove Stripe Customer By Id
     * @param customerId
     */
    async removeStripeCustomer(customerId) {
      return await Wallkit.removeStripeCustomer(customerId);
    },

    /**
     * Handle Stripe Card Action
     * @param response
     */
    async handleStripeCardAction(response) {
      if (!response) throw new Error('No Response Provided as argument');

      if (typeof response !== 'object') throw new Error('Response is not an object');

      let transactions = response.transactions || [response];

      if (Array.isArray(transactions) && transactions.length) {
        let transaction = transactions[0];
        let transactionStatus = transaction.status;

        if (transactionStatus === "succeeded") {
          return {
            status: true,
            message: 'Transaction succeeded'
          };
        } else if (transactionStatus === "requires_source_action") {
          StripeUtils.handle3dSecureModal().catch(() => debug.log('closed'));

          let {
            paymentIntent: payment_intent,
            error
          } = await this.stripe.handleCardAction(transaction.data.client_secret);

          if (error) throw error;

          return await this.confirmCardAction(payment_intent);
        } else {
          return {
            status: false,
            message: 'Transaction failed'
          };
        }
      }
    },

    setupPaymentElements (args){
      debug.log('setupPaymentElements', args);

      const {calculated: {currency}} = args;

      debug.log('currency', currency);

      const options = {
        mode: 'setup',
        currency: 'usd', // required for SetupIntents
        setupFutureUsage: 'off_session',
        paymentMethodTypes: ['card'],
        loader: 'always',
      };

      const elements = this.stripe.elements(options);
      const paymentElement = elements.create('payment');
      debug.log('paymentElement', paymentElement);
      paymentElement.mount('#payment-element');

    },

    addNewPaymentMethod(e) {
      e.preventDefault();
      debug.log('addNewPaymentMethod');
    },

    /**
     *
     * @param name
     * @returns {*}
     */
    getQueryParam(name) {
      let match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
      match = match && decodeURIComponent(match[1].replace(/\+/g, ' '));
      if (typeof match === "undefined" || typeof match === "null" || !match || match === "null" || match === "undefined") {
        return "";
      }
      return match;
    },

    /**
     * GET OBJECT FROM QUERY STRING
     * @returns {*}
     */
    getQuery() {
      try {

        let string = decodeURIComponent(window.location.search.split("?")[1]);
        let match = RegExp("\\[.*\\]|\\{.*\\}").exec(string);
        return JSON.parse(match[0]);

        /*return JSON.parse(
          decodeURIComponent(window.location.search.split("?")[1])
        )*/
      } catch (_) {
        return {};
      }
    },

    async logoutFromFirebase() {
      if (this.$firebase) {

        try {
          await this.$firebase.auth().signOut();
          this.event('wk-firebase-logout');

          return true;
        } catch (error) {
          return Promise.reject(error);
        }

      }

      return false;
    },

    getParentPage() {
      return this.$store.getters['resource/parentPage'];
    },

    toggleOnOutsideClose(value) {
      this.event('wk-event-close-on-wrapper', !!value);
    },

    setSquareError(error) {
      const messageElement = document.querySelector('.sq-card-message');
      if (messageElement) {
        messageElement.innerText = error;
        messageElement.classList.add('sq-visible', 'sq-card-message-error');
      }

      const wrapperElement = document.querySelector('.sq-card-wrapper');
      if (wrapperElement) {
        wrapperElement.classList.add('sq-error');
      }
    },

    initPaymentSystem(element_name) {
      switch (Wallkit.resource.default_payment_system) {
        case 'stripe': {
          this.stripeInit(element_name)
          break;
        }
        case 'square': {
          this.squareInit(element_name)
          break;
        }
        default: {
          this.stripeInit(element_name)
          break;
        }
      }
    },

    squareInit(element_name) {
      let getElement = (element) => this.$_get_element_in_template(element);
      this.square = window.Square
      if (Wallkit.resource.app_id && Wallkit.resource.location_id) {
        const appId = Wallkit.resource.app_id;
        const locationId = Wallkit.resource.location_id;
        this.onModalReady(async () => {
          if (!window.Square) {
            throw new Error('Square.js failed to load properly');
          }

          let paymentForm = document.createElement('form');
          paymentForm.setAttribute('id', 'payment-form');
          getElement(element_name).appendChild(paymentForm);

          let paymentStatusContainer = document.createElement('div');
          paymentStatusContainer.setAttribute('id', 'payment-status-container');

          let cardContainer = document.createElement('div');
          cardContainer.setAttribute('id', 'card-container');

          paymentForm.appendChild(paymentStatusContainer);
          paymentForm.appendChild(cardContainer);

          let payments;
          try {
            payments = this.square.payments(appId, locationId);
            this.square.paymentsObject = payments;
            this.square.card = await this.squareInitializeCard(payments);
          } catch (e) {
            debug.log('SQUARE ERROR:', e);
            const statusContainer = getElement(
              'payment-status-container'
            );
            statusContainer.className = 'missing-credentials';
            statusContainer.style.visibility = 'visible';
            return;
          }
        });
      }

    },
    async squareInitializeCard(payments, element_name = '#card-container') {
      if (payments) {
        const card = await payments.card();
        try {
          await card.attach(element_name)
          return card
        } catch (error) {
          debug.log("Card attach to element error", error)
          return {error: new Error(error)}
        }
      }
      return undefined
    },

    async squareHandleCardAdd(card) {
      this.toggleLoader(true);
      try {
        const token = await this.squareTokenize(card);
        await Wallkit.client.post({
          path: '/user/square/token',
          data: {
            token: token
          }
        }).catch(error => {
          this.setSquareError(error);
          debug.log('Square tokenizing error', error)
          throw new Error(error.message);
        });
        EventBus.$emit('wk-lazy-comp-reload', true);

        this.toggleLoader(false);

        return {result: true};
      } catch (error) {
        debug.log('Square on card input error:', error);
        this.toggleLoader(false);

        return {result: false, error};
      }
    },

    async squareTokenize(paymentMethod) {
      try {
        const tokenResult = await paymentMethod.tokenize();
        if (tokenResult.status === 'OK') {
          return tokenResult.token;
        } else {
          let errorMessage = `Tokenization failed with status: ${tokenResult.status}`;
          if (tokenResult.errors) {
            errorMessage += ` and errors: ${JSON.stringify(
              tokenResult.errors
            )}`;
          }
          throw new Error(errorMessage);
        }
      } catch (error) {
        debug.log('Square tokenize error', error)
        return {error: new Error(error)}
      }
    },

    getCheckoutPromo() {
      return this.$store.getters['user/promo'];
    },

    setCheckoutPromo(promo) {
      return this.$store.dispatch('user/setPromo', promo);
    },

    clearPromo() {
      return this.$store.dispatch('user/clearPromo');
    },

    /**
     * Setup Stripe Payment Element
     * @param args
     */
    stripePaymentElementsSetup (args = {}){

      const options = {
        mode: 'setup',
        currency: 'usd', // required for SetupIntents
        setupFutureUsage: 'off_session',
      };

      this.stripeElements = this.stripe.elements(options);
      const paymentElement = this.stripeElements.create('payment');
      paymentElement.mount('#payment-element');
    },

    /**
     * Submit Stripe Payment Element Form
     * @param e
     * @returns {Promise<*>}
     */
    async stripePaymentElementsSubmit (e) {
      return await this.stripeElements.submit();
    },

    /**
     * Clear Stripe Payment Element
     * @returns {Promise<*>}
     */
    stripePaymentElementsClearPayment () {
      this.stripeElements.getElement('payment').clear();
    },

    /**
     * Get Stripe Customer from Wallkit API
     * if Stripe Customer already exists for current user - return it
     * if Stripe Customer not exists for current user - create it and return
     */
    async stripePaymentElementsGetCustomer() {
      return Wallkit.stripePaymentElementsGetCustomer();
    },

    /**
     * Setup Intents for Stripe Payment Elements
     * @param wallkitCustomer
     */
    async stripePaymentElementsSetupIntents(wallkitCustomer) {
      return await Wallkit.stripePaymentElementsSetupIntents(wallkitCustomer);
    },

    /**
     * Confirm Setup Intents for Stripe Payment Elements
     * @param setupIntents
     */
    async stripePaymentElementsConfirmSetupIntents(setupIntents) {

      try {
        if (!setupIntents) {
          throw new Error('No setupIntents passed as argument');
        }

        const {setup_intent: {client_secret}} = setupIntents;

        if (!client_secret) {
          throw new Error('No client_secret in setupIntents');
        }

        const res = await this.stripe.confirmSetup({
          elements: this.stripeElements,
          clientSecret: client_secret,
          confirmParams: {
            return_url: `${window.location.origin}/#WkModal(confirm-intent)`,
          },
          redirect: 'if_required',
        });

        const {error, setupIntent: confirmedSetupIntent} = res;

        if (error) {
          throw new Error(error.message);
        }

        return confirmedSetupIntent;

      } catch (e) {
        throw new Error(e.message);
      }

    },

    /**
     * Save Intents for future payments
     * @param confirmedSetupIntent
     */
    async stripePaymentElementsSaveIntents(confirmedSetupIntent) {
      return await Wallkit.stripePaymentElementsSaveIntents(confirmedSetupIntent);
    },

    async stripePaymentElementsAddNewPaymentMethod (e){
      try {
        e.preventDefault();
        const stripePaymentElementsSubmitRes = await this.stripePaymentElementsSubmit(e);

        console.log('stripePaymentElementsSubmitRes', stripePaymentElementsSubmitRes);

        // this.toggleLoader(true);

        const errorMessage = get(stripePaymentElementsSubmitRes, 'error.message');
        if (errorMessage) {
          throw new Error(errorMessage);
        }

        // await new Promise(resolve => setTimeout(resolve, 5000));

        const getCustomerRes = await Wallkit.stripePaymentElementsGetCustomer();
        const setupIntentsRes = await Wallkit.stripePaymentElementsSetupIntents(getCustomerRes);
        const confirmSetupIntentsRes = await this.stripePaymentElementsConfirmSetupIntents(setupIntentsRes);
        await this.stripePaymentElementsSaveIntents(confirmSetupIntentsRes);
        await this.stripePaymentElementsClearPayment();

        EventBus.$emit('wk-lazy-comp-reload', true);

      } catch (error) {
        console.log(error);
        const errorDescription = get(error, 'error_description');
        if (errorDescription) {
          throw new Error(errorDescription);
        } else {
          throw new Error(error.message);
        }
      } finally {
        this.toggleLoader(false);
      }

    },

    /**
     * Handle Stripe Action
     *
     * @param response
     * @returns {Promise<{message: string, status: boolean}|*>}
     */
    async stripePaymentElementsHandleAction(response) {

      try {
        if (!response) {
          throw new Error('No Response Provided as argument');
        }

        if (typeof response !== 'object') {
          throw new Error('Response is not an object');
        }

        let transactions = response.transactions || [response];

        if (!Array.isArray(transactions) || transactions.length === 0) {
          throw new Error('No transactions found in response');
        }

        const transaction = transactions[0];
        const transactionStatus = transaction.status;

        switch (transactionStatus) {
          case 'free':
          case 'succeeded': {
            return {
              status: true,
              message: 'Transaction succeeded',
              transactions: transactions
            };
          }
          case 'requires_source_action': {
            StripeUtils.handle3dSecureModal().catch(() => console.log('closed'));

            const { data: { client_secret } }  =  transaction;

            if (!client_secret) {
              throw new Error('No client_secret in transaction');
            }

            const { paymentIntent, error } = await this.stripe.handleCardAction(client_secret);

            if (error) {
              throw error;
            }

            return await this.confirmCardAction(paymentIntent);
          }
          default: {
            return {
              status: false,
              message: 'Transaction failed'
            };
          }
        }

      } catch (e) {
        return {
          status: false,
          message: e.message
        };
      }
    },

    clearTemplateError() {
      this.template.error = false;
      this.template.error_description = '';
    },

    showTemplateError(error) {

      let errorMessage = 'Something went wrong.';

      if ((typeof error === 'string' || error instanceof String) && error.length > 0) {
        errorMessage = error;
      }

      const responseErrorDescription = get(error, 'response.error_description');
      if (responseErrorDescription) {
        errorMessage = responseErrorDescription;
      }

      const errorDescription = get(error, 'error_description');
      if (errorDescription) {
        errorMessage = errorDescription;
      }

      const message = get(error, 'message');
      if (message) {
        errorMessage = message;
      }

      this.template.error = true;
      this.template.error_description = errorMessage;
      this.resize();
    }
  }
}



// WEBPACK FOOTER //
// ./src/extension/methods.js